import './index.css';
import LoginComponent from './components/login-component';
import { DashboardComponent } from './components/dashboard-component';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';
import ErrorPage from './components/error-page';
import { SignUpComponent } from './components/signup-page-component';
import { ForgotPasswordComponent } from './components/forgot-password-component';
import { TermsOfServiceComponent } from './components/terms-of-service-component';
import { ProfileComponent } from './components/profile-component';
import { CompanyComponent } from './components/company-component';
import { ChangelogComponent } from './components/change-log-component';
import { PrivacyPolicyComponent } from './components/privacy-policy-component';
import { MainBackground } from './components/main-background';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useConversationStore } from './store/conversation-store';
import { createCheckoutSession, getUser } from './api/thunks/users';
import { useAppDispatch, useAppSelector } from './store/hooks';
import { TryIt } from './components/try-it-component';
import { createConversation, createConversationShareLink, deleteConversation, deleteConversationShareLink, getAllConversations, getAllSupportedTickers, getConversation, getConversationWithShareLink, updateConversation } from './api/thunks/conversations';
import { setNotification } from './store/notifications-reducer';
import { PricingComponent } from './components/pricing-component';
import { ComparisonComponent } from './components/comparison-component';
import ImageModal, { ImgModalState } from './components/image-modal';
import { CodeModal } from './components/code-modal';
import { BundledLanguage, BundledTheme, createHighlighter, HighlighterGeneric } from "shiki";
import { EarningsCalendarComponent } from './components/earnings-calendar-component';
import { EarningsCalendarItem } from './api/models/EarningsCalendar';
import axios from 'axios';
import { v4 } from 'uuid';
import { ArtifactsPage } from './components/artifacts-component';
import { Artifact } from './api/models/Artifact';
import { createArtifact, deleteArtifact, getAllArtifacts } from './api/thunks/artifacts';
import { WorkflowsPage } from './components/workflows-page';
import { getAllWorkflows } from './api/thunks/workflows';
import { Workflow } from './api/models/Workflows';
import { getAssistants } from './api/thunks/assistants';
import { Assistant } from './api/models/Assistant';
import CongressionalDashboard from './components/congressional-dashboard';
import { Secret } from './api/models/Secret';
import { getSecrets } from './api/thunks/secrets';
import { BlogPage } from './components/blog-page';
import { BlogPost, getAllBlogPosts } from './api/thunks/blogs';
import { BlogPostPage } from './components/blog-post-page';
import { LearningCenterPage } from './components/learning-center-page';
import { CaseStudyPage } from './components/case-study-page';
import { EventsPage } from './components/events-page';
import { HeaderNavComponent } from './components/header-nav-compontent';
import { SplashPageRouter } from './components/splash-page-router';
import { CheckoutComponent } from './components/checkout-component';
import { AntiScrapingComponent } from './components/anti-scraping-component';
import { setCredentials } from './store/user-reducer';
import NotificationPopup from './components/notification-popup-component';
import { ChatTransport } from './chat/chat-transport';

const DEFAULT_ARTIFACT_PAGE_SIZE = 12;

export function App() {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.user.user);
  const credentials = useAppSelector((state) => state.user.credentials);

  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [earningsCalendar, setEarningsCalendar] = useState<EarningsCalendarItem[]>([]);

  const [imgModalState, setImgModalState] = useState<ImgModalState>({
    isImageModalOpen: false,
    modalImageSrc: '',
  });

  const [highlighter, setHighlighter] = useState<HighlighterGeneric<BundledLanguage, BundledTheme>>()
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentCode, setCurrentCode] = useState<string | null>(null);

  const convo = useConversationStore((s) => s.currentConversation);
  const allConvos = useConversationStore((s) => s.conversations);
  const setAllConvos = useConversationStore((s) => s.setConversations);
  const [supportedTickers, setSupportedTickers] = useState<string[]>([]);
  const [shouldShowSpotlight, setShouldShowSpotlight] = useState<boolean>(allConvos.length === 0);
  const [artifacts, setArtifacts] = useState<Artifact[]>([]);
  const [artifactPagination, setArtifactPagination] = useState({
    page: 1,
    pageSize: DEFAULT_ARTIFACT_PAGE_SIZE,
    total: 0,
    totalPages: 1,
    hasMore: false,
  });
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [assistants, setAssistants] = useState<Assistant[]>([]);
  const [isShareMode, setIsShareMode] = useState(false);
  const [userSecrets, setUserSecrets] = useState<Secret[]>([]);

  const chatTransport = useRef<ChatTransport | null>(null);
  if (chatTransport.current === null) chatTransport.current = new ChatTransport();
  const handleCreateConversation = async (value: string) => {
    if (!credentials) {
      dispatch(setNotification({ type: 'info', message: 'Please log in to create a conversation' }));
      return
    };
    createConversation({ name: value, version: 'v3' }, credentials.accessToken, dispatch).then((res) => {
      if (res?.id) {
        window.location.href = '/dashboard?conversationId=' + res.id;
      }
    }).catch(() => {
      dispatch(setNotification({ type: 'error', message: 'Failed to create conversation' }));
    });
  }

  const openCodeModal = (code: string) => {
    setCurrentCode(code);
    setIsModalOpen(true);
  };

  const closeCodeModal = () => {
    setCurrentCode(null);
    setIsModalOpen(false);
  };

  const handleDeleteArtifact = (id: string) => {
    if (!credentials) return;

    deleteArtifact(id, credentials.accessToken).then(() => {
      dispatch(setNotification({ type: 'success', message: 'Artifact deleted successfully' }));

      const remainingOnPage = artifacts.length - 1;
      const shouldGoToPreviousPage = remainingOnPage <= 0 && artifactPagination.page > 1 && !artifactPagination.hasMore;
      const nextPage = shouldGoToPreviousPage ? artifactPagination.page - 1 : artifactPagination.page;

      loadArtifacts(nextPage, artifactPagination.pageSize);
    }).catch((error) => {
      dispatch(setNotification({ type: 'error', message: error.message }));
    });
  }

  const handleDeleteConversation = async (id: string) => {
    if (!credentials) return;
    deleteConversation(id, credentials.accessToken, dispatch).then(async () => {
      getAllConversations(credentials.accessToken).then((updated) => {
        if (updated.length > 0) {
          window.location.href = `/dashboard?conversationId=${updated[0].id}`;
        } else {
          createConversation({
            version: 'v3'
          }, credentials.accessToken, dispatch).then((res) => {
            if (res?.id) {
              window.location.href = '/dashboard?conversationId=' + res.id;
            }
          }
          ).catch(() => {
            dispatch(setNotification({ type: 'error', message: 'Failed to create new conversation' }));
          });
        }
      }).catch(() => {
        dispatch(setNotification({ type: 'error', message: 'Failed to get conversations' }));
      });
    }).catch(() => {
      dispatch(setNotification({ type: 'error', message: 'Failed to delete conversation' }));
    })
  }

  const handleCreateArtifact = async (type: 'code' | 'image', data: string, hint?: string) => {
    if (!credentials) return;
    createArtifact({ artifactType: type, data: data, hint: hint }, credentials.accessToken).then((id) => {
      dispatch(setNotification({ type: 'success', message: 'Artifact created successfully' }));
      loadArtifacts(1, artifactPagination.pageSize);
    }).catch((error) => {
      dispatch(setNotification({ type: 'error', message: 'Failed to create artifact' }));
    });
  }

  const loadArtifacts = useCallback((page: number, pageSize: number) => {
    if (!credentials) return;

    getAllArtifacts(credentials.accessToken, { page, pageSize }).then((res) => {
      setArtifacts(res.items);
      setArtifactPagination({
        page: res.page,
        pageSize: res.pageSize,
        total: res.total,
        totalPages: res.totalPages,
        hasMore: res.hasMore,
      });
    }).catch(() => {
      dispatch(setNotification({
        message: 'Failed to get artifacts',
        type: 'error'
      }));
    });
  }, [credentials, dispatch]);

  const handleArtifactsPageChange = (page: number) => {
    const normalizedPage = Math.max(1, page);
    loadArtifacts(normalizedPage, artifactPagination.pageSize);
  };

  const handleArtifactsPageSizeChange = (pageSize: number) => {
    const normalizedPageSize = Math.min(Math.max(pageSize, 1), 50);
    setArtifactPagination((prev) => ({
      ...prev,
      page: 1,
      pageSize: normalizedPageSize,
    }));
    loadArtifacts(1, normalizedPageSize);
  };

  const handleDeleteShareLink = (conversationId: string) => {
    console.log('deleting share link ', conversationId)
    if (!credentials) return;
    deleteConversationShareLink(conversationId, credentials.accessToken).then(() => {
      dispatch(setNotification({
        type: 'success',
        message: 'Share link deleted successfully'
      }));
      useConversationStore.getState().updateConversationShareLink(conversationId, undefined);
    }).catch((error) => {
      dispatch(setNotification({
        type: 'error', message: error
      }));
    })
  };

  const handleUpdateConversation = (id: string, name: string) => {
    if (!credentials) return;
    updateConversation(id, name, credentials.accessToken).then(() => {
      dispatch(setNotification({ type: 'success', message: 'Conversation updated successfully' }));
      useConversationStore.getState().updateConversationName(id, name);
    }).catch(() => {
      dispatch(setNotification({ type: 'error', message: 'Failed to update conversation' }));
    });
  }

  const handleCreateShareLink = async (conversationId: string) => {
    if (!credentials || !convo) {
      return;
    }
    if (convo?.shareLink) {
      navigator.clipboard.writeText(convo?.shareLink);
      dispatch(setNotification({
        message: 'Link copied to clipboard',
        type: 'success'
      }));
      return;
    }
    createConversationShareLink(conversationId, credentials?.accessToken).then((res) => {
      useConversationStore.getState().updateConversationShareLink(conversationId, res);

      navigator.clipboard.writeText(res).then(() => {
        dispatch(setNotification({
          message: 'Link created and copied to clipboard',
          type: 'success'
        }));
      }).catch((e) => {
        dispatch(setNotification({
          message: 'Share Link: ' + res,
          type: 'success'
        }));
      });
    }).catch((e) => {
      dispatch(setNotification({
        message: 'Failed to create share link',
        type: 'error'
      }))
    })
  }

  const router = createBrowserRouter([
    {
      path: "/health",
      element: <div>Healthy</div>,
    },
    {
      path: "/:variant?",
      element: <SplashPageRouter />,
      errorElement: <ErrorPage />,
    },
    {
      path: "/company",
      element: <CompanyComponent />
    },
    {
      path: "/changelog",
      element: <ChangelogComponent />
    },
    {
      path: '/terms-of-service',
      element: <TermsOfServiceComponent></TermsOfServiceComponent>,
    },
    {
      path: 'privacy-policy',
      element: <PrivacyPolicyComponent></PrivacyPolicyComponent>,
    },
    {
      path: "/profile",
      element:
        <MainBackground>
          <ProfileComponent handleCreateShareLink={handleCreateShareLink} handleUpdateConversation={handleUpdateConversation} convo={convo} convos={allConvos} handleCreateConversation={handleCreateConversation} handleDeleteConversation={handleDeleteConversation} supportedTickers={supportedTickers} handleDeleteShareLink={handleDeleteShareLink} secrets={userSecrets} setSecrets={setUserSecrets} />
        </MainBackground>,
    },
    {
      path: '/signup',
      element: <SignUpComponent></SignUpComponent>,
      errorElement: <ErrorPage />,
    },
    {
      path: '/password-reset',
      element: <ForgotPasswordComponent />,
      errorElement: <ErrorPage />,
    },
    {
      path: "/login",
      element: <LoginComponent></LoginComponent>,
      errorElement: <ErrorPage />,
    },
    {
      path: "/dashboard",
      element:
        <>
          <NotificationPopup />
          <ImageModal
            imgName=''
            isOpen={imgModalState.isImageModalOpen}
            imageSrc={imgModalState.modalImageSrc}
            onClose={() => setImgModalState({ ...imgModalState, isImageModalOpen: false })}
          />
          {currentCode && (
            <CodeModal
              isOpen={isModalOpen}
              code={currentCode}
              onClose={closeCodeModal}
              highlighter={highlighter}
              language='python'
              openCodeModal={openCodeModal}
              createArtifact={(type, data) => handleCreateArtifact(type, data, undefined)}
            />
          )}
          {/* <SpotlightSearchOverlay placeholder='Search companies by ticker...' searchValues={supportedTickers} handleSearchValueClicked={handleCreateConversation} show={shouldShowSpotlight} /> */}
          {/* <MainBackground> */}
            <DashboardComponent
              handleUpdateConversation={handleUpdateConversation}
              isShareMode={isShareMode}
              setAllConvos={setAllConvos}
              handleCreateArtifact={handleCreateArtifact}
              handleCreateConversation={handleCreateConversation}
              supportedTickers={supportedTickers}
              convos={allConvos}
              openCodeModal={openCodeModal}
              setImgModalState={setImgModalState}
              handleDeleteConversation={handleDeleteConversation}
              handleDeleteShareLink={handleDeleteShareLink}
              handleCreateShareLink={handleCreateShareLink}
              // During a React Fast Refresh the ref can still contain an
              // instance from the prior module version. Keep sending usable
              // while that old instance is replaced on the next reload.
              expectResponse={(conversationId) => chatTransport.current?.expectResponse?.(conversationId)}
              onWorkflowCreated={() => {
                if (credentials?.accessToken) {
                  getAllWorkflows(credentials.accessToken).then((workflows) => {
                    setWorkflows(workflows);
                  }).catch((err) => {
                    console.error('Failed to refresh workflows after creation', err);
                  });
                }
              }}
            />
          {/* </MainBackground> */}
        </>,
      errorElement: <ErrorPage />,
    },
    {
      path: '/try-it',
      element: <MainBackground>
        <TryIt showNav={true} showTabs={true} />
      </MainBackground>
    },
    {
      path: '/pricing',
      element:
        <MainBackground>
          <PricingComponent showNav={true} />
        </MainBackground>
    },
    {
      path: '/pq-v-chatgpt',
      element:
        <MainBackground >
          <ComparisonComponent showNav={true} />
        </MainBackground>,
    },
    {
      path: '/earnings-calendar',
      element: <MainBackground>
        <HeaderNavComponent />
        <EarningsCalendarComponent calendar={earningsCalendar} />
      </MainBackground>
    },
    {
      path: '/artifacts',
      element: <MainBackground>
        <ArtifactsPage
          handleUpdateConversation={handleUpdateConversation}
          artifacts={artifacts}
          pagination={artifactPagination}
          onPageChange={handleArtifactsPageChange}
          onPageSizeChange={handleArtifactsPageSizeChange}
          convo={convo}
          convos={allConvos}
          handleCreateConversation={handleCreateConversation}
          supportedTickers={supportedTickers}
          highlighter={highlighter}
          handleDeleteArtifact={handleDeleteArtifact}
          handleDeleteConversation={handleDeleteConversation}
          handleDeleteShareLink={handleDeleteShareLink}
          handleCreateShareLink={handleCreateShareLink}
        />
      </MainBackground>
    },
    {
      path: '/workflows',
      element: <MainBackground>
        <WorkflowsPage
          handleUpdateConversation={handleUpdateConversation}
          workflows={workflows}
          setWorkflows={setWorkflows}
          convo={convo}
          convos={allConvos}
          assistants={assistants}
          handleCreateConversation={handleCreateConversation}
          handleDeleteConversation={handleDeleteConversation}
          supportedTickers={['all', ...supportedTickers]}
          handleDeleteShareLink={handleDeleteShareLink}
          handleCreateShareLink={handleCreateShareLink}
          userSecrets={userSecrets}
        />
      </MainBackground>
    },
    {
      path: '/test',
      element: <MainBackground>
        <CongressionalDashboard
          onDataRefresh={() => {
            // Implement data refresh logic here
            console.log('Refreshing congressional data...');
          }}
        />
      </MainBackground>
    },
    {
      path: '/blogs',
      element: <MainBackground>
        <BlogPage posts={blogPosts} />
      </MainBackground>
    },
    {
      path: '/blogs/:postId/:slug',
      element:
        <MainBackground>
          <BlogPostPage allPosts={blogPosts} />
        </MainBackground>
    },
    {
      path: '/learning-center',
      element: <MainBackground>
        <LearningCenterPage />
      </MainBackground>
    },
    {
      path: '/case-study',
      element: <MainBackground>
        <CaseStudyPage />
      </MainBackground>
    },
    {
      path: '/events',
      element: <MainBackground>
        <EventsPage />
      </MainBackground>
    },
    {
      path: "/checkout",
      element: <MainBackground>
        <CheckoutComponent />
      </MainBackground>,
    },
    {
      path: "/anti-scraping",
      element: <MainBackground>
        <AntiScrapingComponent />
      </MainBackground>,
    },
    {
      path: "/congressional-data",
      element: <MainBackground>
        <CongressionalDashboard
          onDataRefresh={() => {
            // Implement data refresh logic here
            console.log('Refreshing congressional data...');
          }}
        />
      </MainBackground>,
    }
  ]);

  const getEarningsCalendar = async () => {
    try {
      const response = await axios.get(process.env.REACT_APP_API_URL + '/v1/earnings_calendar',
        {
          headers: {
            'X-Request-ID': v4()
          }
        }
      );

      if (response.status !== 200) {
        // dispatch(setNotification({
        //   message: "Unable to get earnings calendar",
        //   type: 'error'
        // }))

        return;
      }

      setEarningsCalendar(response.data as EarningsCalendarItem[]);
    } catch (e) {
      console.log(e)
      // dispatch(setNotification({
      //   message: "Unable to get earnings calendar",
      //   type: 'error'
      // }))
    }
  }

  const handleDemoLogin = () => {
    const query = new URLSearchParams(window.location.search);
    const idToken = query.get("id_token");
    const refreshToken = query.get("refresh_token");
    const demoMigrated = query.get("demo_migrated");

    if (idToken && refreshToken) {
      try {
        console.log('Processing OAuth tokens', { idToken: idToken.substring(0, 20) + '...', refreshToken: refreshToken.substring(0, 20) + '...' });

        // Clean up URL first to prevent reprocessing
        query.delete("id_token");
        query.delete("refresh_token");
        query.delete("demo_migrated");
        window.history.replaceState({}, document.title, window.location.pathname + (query.toString() ? '?' + query.toString() : ''));

        // Set the new credentials
        console.log('setting creds')
        dispatch(setCredentials({ accessToken: idToken, refreshToken }));
        console.log('creds set to url vals')

        // Show success message if this was a demo migration
        if (demoMigrated === 'true') {
          dispatch(setNotification({
            message: 'Successfully upgraded your demo account with Google!',
            type: 'success'
          }));
        }

      } catch (error) {
        console.error('Failed to set credentials from OAuth:', error);
        dispatch(setNotification({
          message: 'Failed to process OAuth response',
          type: 'error'
        }));
      }
    }
  }

  useEffect(() => {
    if (!credentials) return;

    const accessToken = credentials.accessToken;

    localStorage.setItem('credentials', JSON.stringify(credentials));
    chatTransport.current?.start({
      accessToken,
      getConversation,
      refreshConversationList: async () => {
        const conversations = await getAllConversations(accessToken);
        useConversationStore.getState().setConversations(conversations);
      },
      onFunctionCall: (name, rawArgs) => {
        let args: Record<string, any> = {};
        try { args = typeof rawArgs === 'string' ? JSON.parse(rawArgs) : (rawArgs || {}); } catch { /* tool notifications are best-effort */ }
        const title = name.replace(/_/g, ' ').replace(/\b\w/g, (character) => character.toUpperCase());
        const message = name === 'get_financial_ratio'
          ? `Computing ${args.fiscalPeriod}-${args.fiscalYear} ${args.ratio || 'financial ratio'}`
          : name === 'get_earnings_statement_item'
            ? `Looking up earnings data ${args.item || ''} ${args.fiscalPeriod}-${args.fiscalYear}`
            : name === 'render_ui_table'
              ? `Rendering table: ${args.title || 'Data Table'}`
              : title;
        dispatch(setNotification({ message, type: 'info' }));
      },
      onWorkflowUpdate: (message) => {
        if (message) dispatch(setNotification({ message, type: 'info' }));
        getAllWorkflows(accessToken).then(setWorkflows).catch(() => dispatch(setNotification({ message: 'Failed to get workflows', type: 'error' })));
      },
      onFreeCreditUsed: () => { dispatch(getUser()); },
      onReconcileError: () => dispatch(setNotification({ message: 'Response completed, but could not refresh it. It will appear on reload.', type: 'error' })),
    });
    dispatch(getUser());
    const query = new URLSearchParams(window.location.search);
    const conversationId = query.get('conversationId');

    if (query.get("redirect") === "checkout" && query.get('price_id') !== null) {
      dispatch(createCheckoutSession({ productCode: query.get('price_id')! })).then((checkoutRes) => {
        console.log('checkoutRes ', checkoutRes)
        if (checkoutRes.meta.requestStatus === 'fulfilled') {
          window.location.href = (checkoutRes.payload as any).redirectUrl;
        } else {
          alert('Failed to create checkout session 1' + JSON.stringify(checkoutRes));
          return
        }
      }).catch((err) => {
        console.log('err ', err);
        alert('Failed to create checkout session 2');
        return
      })

      return () => chatTransport.current?.stop();
    }

    getAllConversations(accessToken).then((res) => {
      useConversationStore.getState().setConversations(res);
      if (!conversationId && res.length > 0) {
        query.set('conversationId', res[0].id);
        window.location.href = window.location.pathname + '?' + query.toString();
      }
    }).catch((err) => {
      dispatch(setNotification({
        message: 'Failed to get conversations',
        type: 'error'
      }));
    });

    if (conversationId && window.location.href.includes('dashboard')) {
      getConversation(conversationId, accessToken).then((res) => {
        useConversationStore.getState().setCurrentConversation(res);
      }).catch((err) => {
        dispatch(setNotification({
          message: 'Failed to get conversation',
          type: 'error'
        }));
      });
    }

    getAllSupportedTickers({ accessToken }).then((res) => {
      setSupportedTickers(res);
    }).catch((err) => {
      dispatch(setNotification({
        message: 'Failed to get supported tickers',
        type: 'error'
      }));
    });

    setArtifactPagination({
      page: 1,
      pageSize: DEFAULT_ARTIFACT_PAGE_SIZE,
      total: 0,
      totalPages: 1,
      hasMore: false,
    });
    loadArtifacts(1, DEFAULT_ARTIFACT_PAGE_SIZE);

    getAllWorkflows(accessToken).then((workflows) => {
      setWorkflows(workflows);
    }).catch(() => {
      dispatch(setNotification({
        message: 'Failed to get workflows',
        type: 'error'
      }))
    });

    getAssistants(accessToken).then((assistants) => {
      setAssistants(assistants);
    }).catch(() => {
      dispatch(setNotification({
        message: 'Failed to get assistants',
        type: 'error'
      }))
    });

    getSecrets(accessToken).then((secrets) => {
      setUserSecrets(secrets);
    }).catch(() => {
      dispatch(setNotification({
        message: 'Failed to get user secrets',
        type: 'error'
      }))
    })

    return () => chatTransport.current?.stop();
  }, [credentials, dispatch, loadArtifacts])

  useEffect(() => {
    if (user?.hasActiveSubscription !== true) {
      return;
    }

    console.log("sending purchase conversion event")
    let value = 1; // pay as you go will be considered $1
    if (user.plan === 'pro') {
      value = 20;
    } else if (user.plan === 'premium') {
      value = 49;
    }
    gtag('event', 'conversion', {
      'send_to': 'AW-16667259928/KabTCJq7hpQaEJjoyIs-',
      'value': value,
      'currency': 'USD',
      'transaction_id': user?.id
    });
    console.log('done sending purchase conversion event');
  }, [user])

  // Handle OAuth tokens on mount - separate from credentials useEffect to prevent infinite loop
  useEffect(() => {
    handleDemoLogin();
  }, []); // Run only once on mount

  useEffect(() => {

    getAllBlogPosts().then((posts) => {
      setBlogPosts(posts);
    }).catch((err) => {
      console.log('failed to get blog posts', err);
      dispatch(setNotification({
        message: 'Failed to get blog posts',
        type: 'error'
      }))
    })

    getEarningsCalendar()

    createHighlighter({ themes: ["github-dark"], langs: ["python"] }).then((h) => {
      setHighlighter(h);
    });

    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === " " || e.key === 's')) {
        e.preventDefault();
        setShouldShowSpotlight((prev) => !prev)
      }

      if (e.key === 'Escape' && shouldShowSpotlight) {
        setShouldShowSpotlight(false);
      }
    };

    const query = new URLSearchParams(window.location.search);
    if (window.location.href.includes('dashboard') && query.get('share') !== null) {
      const shareLinkId = query.get('share');
      if (shareLinkId) {
        setIsShareMode(true);
        getConversationWithShareLink(shareLinkId).then((res) => {
          useConversationStore.getState().setCurrentConversation(res);
        }).catch((err) => {
          dispatch(setNotification({
            message: 'Failed to get shared conversation',
            type: 'error'
          }));
        });

  getAllSupportedTickers({ shareLinkToken: shareLinkId }).then((res) => {
          setSupportedTickers(res);
        }).catch((err) => {
          dispatch(setNotification({
            message: 'Failed to get supported tickers',
            type: 'error'
          }));
        });
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <RouterProvider router={router} />
  );
}
